import { useEffect } from "react";
import { X, Layers3, Sparkles, Clock, Zap } from "lucide-react";

/**
 * StemSeparateModal — small centred picker that lets the user choose
 * between a standard 4-stem split and an advanced 10-stem split before
 * firing `/api/production/stem-separate`.
 *
 * Props:
 *   open           bool — visibility
 *   onClose()      — called on Esc / backdrop / X / Cancel
 *   onConfirm(adv) — receives `advanced: bool` when the user hits Start
 *   busy           bool — disables the confirm button + shows a spinner label
 *   sourceLabel    string — shown in the header for context (e.g. "song.mp3")
 */
export default function StemSeparateModal({ open, onClose, onConfirm, busy, sourceLabel }) {
  useEffect(() => {
    if (!open) return;
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="stem-sep-modal-backdrop"
    >
      <div
        className="relative w-full max-w-lg bg-[#0f1117] border border-[#2a2f3a] rounded-lg shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="stem-sep-modal"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1 text-[#a9b3c9] hover:text-white transition"
          aria-label="Close"
          data-testid="stem-sep-modal-close"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Layers3 size={16} className="text-[#4a9eff]" />
          <div className="type-section-heading text-white">Split into stems</div>
        </div>
        {sourceLabel ? (
          <div className="type-list-subtitle text-[#a9b3c9] mb-5 truncate" title={sourceLabel}>
            Source: <span className="text-white/80">{sourceLabel}</span>
          </div>
        ) : (
          <div className="type-list-subtitle text-[#a9b3c9] mb-5">
            LALAL.AI runs first; Replicate/Demucs is the automatic fallback.
          </div>
        )}

        {/* ── Standard (4 stems) ────────────────────────────────── */}
        <button
          type="button"
          onClick={() => !busy && onConfirm(false)}
          disabled={busy}
          className="w-full text-left mb-3 p-4 rounded-md border border-[#2a2f3a] hover:border-[#4a9eff] bg-[#171a24] hover:bg-[#1a1f2e] transition disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="stem-sep-mode-standard"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Zap size={13} className="text-[#3ecf8e]" />
              <div className="type-list-title text-white">Standard · 4 stems</div>
            </div>
            <div className="type-status-badge text-[#3ecf8e] bg-[#3ecf8e]/10 px-2 py-0.5 rounded">
              Fast
            </div>
          </div>
          <div className="type-list-subtitle text-[#a9b3c9]">
            Vocals · Drums · Bass · Other. Fastest, lowest LALAL.AI minute cost.
          </div>
        </button>

        {/* ── Advanced (10 stems) ───────────────────────────────── */}
        <button
          type="button"
          onClick={() => !busy && onConfirm(true)}
          disabled={busy}
          className="w-full text-left p-4 rounded-md border border-[#2a2f3a] hover:border-[#FF8A1F] bg-[#171a24] hover:bg-[#1a1f2e] transition disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="stem-sep-mode-advanced"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles size={13} className="text-[#FF8A1F]" />
              <div className="type-list-title text-white">Advanced · 10 stems</div>
            </div>
            <div className="type-status-badge text-[#FF8A1F] bg-[#FF8A1F]/10 px-2 py-0.5 rounded">
              LALAL.AI only
            </div>
          </div>
          <div className="type-list-subtitle text-[#a9b3c9] mb-2">
            Vocals · Drums · Bass · Piano · Electric &amp; Acoustic Guitar · Synth · Strings · Wind · Other.
          </div>
          <div className="flex items-center gap-1 text-[#f5b942] type-status-badge">
            <Clock size={11} />
            <span>Slower · uses more LALAL.AI minutes (billed per stem).</span>
          </div>
        </button>

        <div className="flex items-center justify-between mt-5">
          <div className="type-list-subtitle text-[#7a8399]">
            {busy ? "Splitting…" : "Choose a mode to start"}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-[#a9b3c9] hover:text-white transition"
            data-testid="stem-sep-modal-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
